<template>
  <div class='user-choose-location' slot='content'>
    <div class='user-choose-location-autocomplete'>
      <gmap-autocomplete
        @place_changed="setSelectedPlaceTemp"
        placeholder='Enter zip code or city/state'
        class='user-choose-location-autocomplete__input input'
      />
      <button 
        class='button is-primary'
        @click='setSelectedPlace()'
      >
        Use
      </button>
    </div>
    <div class='user-choose-location__map-container'>
      <transition name='loading-fade'>
        <div 
          class='user-choose-location__loader'
          v-show='showMapLoadingOverlay'
        >
          <loader 
            :width='50'
            :height='50'
            color='#dedede'
            message='Geocoding point. Please wait...'
          />
        </div>
      </transition>
      <google-map
        class='user-choose-location-map'
        @mapReady='onMapReady'
        @mapClicked='onMapClicked'
      >
        <gmap-marker 
          :position='selectedLocation'
        />
      </google-map>
    </div>
  </div>
</template>

<script>
import userGeolocation from '@/util/userGeolocation'
import geocoder from '@/util/geocoder'

import GoogleMap from 'LocalComponents/GoogleMap'
import Loader from 'Components/Shared/Loader'

export default {
  name: 'UserChooseLocation',

  mixins: [userGeolocation],
  
  components: {
    GoogleMap,
    Loader,
  },

  props: {
    /**
     * The initial userPosition
     */
    intialUserPosition: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data () {
    return {
      selectedLocation: this.intialUserPosition && {
        lat: this.intialUserPosition.latitude,
        lng: this.intialUserPosition.longitude,
      },
      selectedPlace: null,
      selectedPlaceTemp: null,
      google: null,
      geocoder: null,
      showMapLoadingOverlay: false,
      map: null,
    }
  },

  mounted () {
    this.getUserLocation()
  },

  methods: {
    /**
     * Triggers when the map is ready
     * 
     * @param {Object} map
     */
    onMapReady (map, google) {
      this.google = google
      this.map = map
      this.map.setOptions({
        draggableCursor: 'pointer',
      })
    },

    /**
     * Triggers when the map is clicked
     */
    onMapClicked (event) {
      const { latLng } = event

      this.geocodeLocation(latLng)
    },

    /**
     * Sets the user position
     * 
     * @param {Object} latLng
     */
    setSelectedLocation (latLng) {
      this.selectedLocation = latLng
    },

    /**
     * Sets the selected place to a temporary variable 
     * from the auto complete
     * 
     * @param {Object} place
     */
    setSelectedPlaceTemp (place) {
      this.selectedPlaceTemp = place
    },

    /**
     * Sets the selectedPlace to the temporarily stored variable.
     * 
     * @param {Object} place
     */
    setSelectedPlace (place = this.selectedPlaceTemp) {
      this.selectedPlace = place

      this.setSelectedLocation(place.geometry.location)
    },

    /**
     * Reverse gecodes a point
     * 
     * @param {Object} latLng
     * 
     * @returns Object
     */
    async geocodeLocation (latLng) {
      if (!this.geocoder) {
        this.geocoder = new this.google.maps.Geocoder
      }

      this.showMapLoadingOverlay = true

      const location = await geocoder.geocodeLocation(this.geocoder, latLng)

      this.showMapLoadingOverlay = false

      this.setSelectedPlace(location)

      this.$emit('placeChanged', this.selectedPlace, this.selectedLocation)
    },
  },

  watch: {
    selectedPlace (place) {
      // Every time a place is changed, we fit the map's bounds
      // according to the place.
      let bounds = new this.google.maps.LatLngBounds()

      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport)
      } else {
        bounds.extend(place.geometry.location)
      }

      this.map.fitBounds(bounds)
    },
  },
}
</script>

<style lang="scss" scoped>
.user-choose-location {
  &__map-container {
    height: 300px;
    width: 100%;
    position: relative;
  }

  &__loader {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background-color: rgba(#000, .3);
    color: #fff;

    p {
      text-align: center;
      margin-top: .5em;
      color: #fff;
    }
  }
}

.user-choose-location-map {
  position: absolute;
  height: 100%;
  width: 100%;
}

.user-choose-location-autocomplete {
  margin-bottom: 1em;
  display: flex;
  justify-content: flex-end;

  &__input {
    max-width: 250px;
    margin-right: .5em;
  }
}

.user-choose-location-actions {
  text-align: right;
  margin-top: 1em;
  &__button {
    &:not(:last-child) {
      margin-right: .5em;
    }
  }
}

// Transitions
.loading-fade-enter-active, .loading-fade-leave-active {
  transition: opacity .5s;
}
.loading-fade-enter, .loading-fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
